import React, { type ReactElement, useId, useMemo } from 'react';
import { FormGroup } from 'reactstrap';
import { NumberParam, type UrlUpdateType, useQueryParam } from 'use-query-params';
import Label from 'atom-components/label';
import Select from 'atom-components/select/select';
import generateBands from 'feature-components/reports/item-assessment-report/helpers/generate-bands';

const BandParam = NumberParam;

export function useBandQueryParams(): {
  minBand: number | null | undefined;
  maxBand: number | null | undefined;
  setMinBand: (newValue: number | undefined, updateType?: UrlUpdateType) => void;
  setMaxBand: (newValue: number | undefined, updateType?: UrlUpdateType) => void;
} {
  const [minBand, setMinBand] = useQueryParam('min-band', BandParam);
  const [maxBand, setMaxBand] = useQueryParam('max-band', BandParam);

  return { minBand, maxBand, setMinBand, setMaxBand };
}

type Props = {
  label: string;
  minScaleScore: number | undefined;
  maxScaleScore: number | undefined;
};

export default function SelectBand({ label, minScaleScore, maxScaleScore }: Props): ReactElement {
  const inputId = useId();

  const bands = useMemo(
    () =>
      minScaleScore !== undefined && maxScaleScore !== undefined
        ? generateBands(minScaleScore, maxScaleScore)
        : [],
    [maxScaleScore, minScaleScore]
  );

  const { minBand, setMinBand, maxBand, setMaxBand } = useBandQueryParams();
  const selectedOption = useMemo(
    () => bands.find((option) => option.min === minBand && option.max === maxBand),
    [bands, maxBand, minBand]
  );

  return (
    <div>
      <Label className="fw-bold" htmlFor={inputId}>
        {label}
      </Label>
      <FormGroup className="d-flex">
        <Select
          inputId={inputId}
          options={bands}
          value={selectedOption}
          getOptionValue={(o): string => o.id}
          onChange={(item): void => {
            setMinBand(item?.min, 'replaceIn');
            setMaxBand(item?.max, 'replaceIn');
          }}
          // treat the existence of values as a pseudo loading indicator
          isLoading={minScaleScore === undefined || maxScaleScore === undefined}
          isMulti={false}
          isClearable
        />
      </FormGroup>
    </div>
  );
}
